import { apiService } from "./api.service";

export interface ReleaseNotesFormData {
  releaseVersion: string;
  subReleaseVersion?: string;
  tagline?: string;
  date: string;
  appNotes: string;
}

export interface ReleaseNotesSubmitPayload {
  details: string;
  line?: string;
  release?: string;
  releaseDate: string;
  version: string;
}

export interface ReleaseNote {
  id?: string;
  version: number | string; // Main version number (e.g., 3, 4, 5, 6)
  release: string; // Sub-release version (e.g., "1.5", "1.4")
  releaseDate: string;
  line?: string;
  details: string;
  createdAt?: string;
}

export class ReleaseNotesService {
  private static SUBMIT_PATH = "/api/v1/forms";

  static async submitReleaseNotes(data: ReleaseNotesFormData): Promise<{ success: boolean; message?: string }> {
    // Format date to DD/MM/YY format
    // DatePicker returns date in YYYY-MM-DD format
    const [year, month, day] = data.date.split("-");
    const releaseDate = `${day}/${month}/${year}`;

    const payload: ReleaseNotesSubmitPayload = {
      details: data.appNotes,
      version: data.releaseVersion,
      releaseDate: releaseDate,
    };

    // Add optional fields only if they have values
    if (data.subReleaseVersion?.trim()) {
      payload.release = data.subReleaseVersion.trim();
    }

    if (data.tagline?.trim()) {
      payload.line = data.tagline.trim();
    }

    const response = await apiService.post(`${this.SUBMIT_PATH}/submitUserReleaseNotes`, payload);
    if (!response.success) {
      return {
        success: false,
        message: response.data?.message || "Failed to submit release notes",
      };
    }

    return { success: true };
  }

  static async getReleases(): Promise<ReleaseNote[]> {
    const response = await apiService.get(`${this.SUBMIT_PATH}/releases`);
    if (!response.success) return [];

    // Handle both array response and object with data property
    if (Array.isArray(response.data)) {
      return response.data as ReleaseNote[];
    }
    if (response.data?.data && Array.isArray(response.data.data)) {
      return response.data.data as ReleaseNote[];
    }
    return [];
  }

  static async getLatestRelease(): Promise<ReleaseNote | null> {
    const response = await apiService.get(`${this.SUBMIT_PATH}/latestRelease`);
    if (!response.success) return null;

    return response.data as ReleaseNote;
  }
}
